import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fab,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AvailabilityScheduleCard from '../components/users/AvailabilityScheduleCard';
import CallPriorityField from '../components/users/CallPriorityField';
import { useUser } from '../hooks/useUser';
import { deleteUserByRole, fetchUserByRole, updateUserByRole } from '../services/users';
import { normalizeCallPriority } from '../utils/callPriority';

const DOCTOR_ROLES = new Set(['CLINIC_CARE_GIVER', 'ACCOUNT_ADMIN']);

const clinicianTypeOptions = [
    { value: 'NURSING', label: 'Enfermagem' },
    { value: 'CARDIOLOGY', label: 'Cardiologia' },
    { value: 'GERIATRICS', label: 'Geriatria' },
    { value: 'NUTRITION', label: 'Nutrição' },
    { value: 'NEUROLOGY', label: 'Neurologia' },
    { value: 'PSYCHIATRY', label: 'Psiquiatria' },
    { value: 'PSYCHOLOGY', label: 'Psicologia' },
    { value: 'PHYSICAL_THERAPY', label: 'Fisioterapia' },
    { value: 'OCCUPATIONAL_THERAPY', label: 'Terapia Ocupacional' },
    { value: 'SOCIAL_ASSISTANT', label: 'Assistente Social' },
    { value: 'GENERAL_PRACTICE', label: 'Medicina Geral' }
];

const actionSecondarySx = {
    bgcolor: '#FFFFFF',
    color: '#3D3D3D',
    border: '2px solid #D9D9D9',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#F7F7F7',
        boxShadow: 'none'
    }
};

const actionPrimarySx = {
    bgcolor: '#D63731',
    color: '#fff',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#C7312C',
        boxShadow: 'none'
    }
};

const readOnlyViewFieldSx = {
    '& .MuiInputBase-input.Mui-disabled': {
        WebkitTextFillColor: '#6F6F6F',
        color: '#6F6F6F'
    },
    '& .MuiInputBase-input.Mui-disabled::placeholder': {
        WebkitTextFillColor: '#8A8A8A'
    },
    '& .MuiSelect-select.Mui-disabled': {
        WebkitTextFillColor: '#6F6F6F',
        color: '#6F6F6F'
    }
};

const dialogPaperSx = {
    borderRadius: '22px',
    boxShadow: '0px 10px 30px rgba(61, 61, 61, 0.12)'
};

const dialogTitleSx = {
    px: 3,
    pt: 3,
    pb: 1.5,
    fontWeight: 700,
    color: '#3D3D3D'
};

const dialogContentSx = {
    px: 3,
    pb: 1,
    pt: 0.5
};

const dialogActionsSx = {
    px: 3,
    pb: 2.5,
    pt: 1
};

const AVAILABILITY_ACTIVE_DAYS_KEY = 'availability_active_days';
const AVAILABILITY_NOTES_KEY = 'availability_notes';
const VALID_AVAILABILITY_DAYS = new Set(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);

const normalizeAvailabilityDays = (value) => {
    const rawDays = Array.isArray(value)
        ? value
        : typeof value === 'string'
            ? value
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean)
            : [];

    return rawDays
        .map((item) => String(item).trim().toLowerCase())
        .filter((item) => VALID_AVAILABILITY_DAYS.has(item));
};

const toApiDate = (date) => {
    if (!date) {
        return undefined;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
    }
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date);
    if (!match) {
        return undefined;
    }
    return `${match[3]}-${match[2]}-${match[1]}`;
};

const toUiDate = (date) => {
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date || '';
    }
    const [y, m, d] = date.split('-');
    return `${d}/${m}/${y}`;
};

const toPickerValue = (value) => {
    if (!value) {
        return null;
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
};

const EditUser = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { roleUuid } = useParams();
    const { user } = useUser();

    const fallbackRole = location.state?.user?.role || '';
    const fallbackName = location.state?.user?.displayName || location.state?.user?.name || '';

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [error, setError] = useState('');
    const [readOnly, setReadOnly] = useState(true);
    const [form, setForm] = useState({
        roleUuid: roleUuid || '',
        role: fallbackRole,
        name: fallbackName,
        email: '',
        birthDate: '',
        street: '',
        zip: '',
        city: '',
        province: '',
        country: '',
        phone: '',
        nif: '',
        socialSecurity: '',
        healthNumber: '',
        professionalCard: '',
        professionalCardExpiry: '',
        workplace: '',
        clinicianType: '',
        callPriority: 5,
        notes: '',
        availabilityActiveDays: [],
        availabilityNotes: '',
        professionalStatus: '',
        occupation: '',
        education: '',
        activeSince: ''
    });
    const [snapshot, setSnapshot] = useState(null);

    const onFieldChange = (field) => (event) => {
        const value = event?.target?.value || '';
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        const loadUser = async () => {
            if (!user.selectedRole || !roleUuid) {
                return;
            }

            setLoading(true);
            setError('');
            try {
                const response = await fetchUserByRole({
                    userRoleUuid: user.selectedRole,
                    roleUuid
                });
                const data = response?.data || {};
                const extra = data.user_data || {};

                const nextForm = {
                    roleUuid: data.role_uuid || roleUuid,
                    role: data.role || fallbackRole,
                    name: data.name || '',
                    email: data.email || '',
                    birthDate: data.birth_date || '',
                    street: data.address?.street || '',
                    zip: data.address?.zip || '',
                    city: data.address?.city || '',
                    province: data.address?.province || '',
                    country: data.address?.country || '',
                    phone: data.phone || '',
                    nif: extra.nif || '',
                    socialSecurity: extra.social_security || '',
                    healthNumber: extra.health_number || '',
                    professionalCard: extra.professional_card || '',
                    professionalCardExpiry: extra.professional_card_expiry || '',
                    workplace: extra.workplace || '',
                    clinicianType: data.clinician_type || '',
                    callPriority: normalizeCallPriority(data.call_priority),
                    notes: extra.notes || '',
                    availabilityActiveDays: normalizeAvailabilityDays(
                        data[AVAILABILITY_ACTIVE_DAYS_KEY] ?? extra[AVAILABILITY_ACTIVE_DAYS_KEY]
                    ),
                    availabilityNotes:
                        data[AVAILABILITY_NOTES_KEY] ?? extra[AVAILABILITY_NOTES_KEY] ?? '',
                    professionalStatus: extra.professional_status || '',
                    occupation: extra.occupation || '',
                    education: extra.education || '',
                    activeSince: toUiDate(extra.active_since || '')
                };

                setForm(nextForm);
                setSnapshot(nextForm);
                setReadOnly(true);
            } catch (err) {
                setError(
                    err?.response?.data?.description ||
                        'Nao foi possivel carregar dados completos. Podes editar manualmente.'
                );
            } finally {
                setLoading(false);
            }
        };

        void loadUser();
    }, [fallbackRole, roleUuid, user.selectedRole]);

    useEffect(() => {
        if (!roleUuid) {
            return;
        }

        if (form.role === 'PATIENT_CARE_GIVER') {
            navigate(`/caregivers/${roleUuid}?tab=personal`, {
                replace: true,
                state: location.state
            });
        }
    }, [form.role, location.state, navigate, roleUuid]);

    const isDoctorForm = DOCTOR_ROLES.has(form.role);
    const isCaregiverForm = form.role === 'PATIENT_CARE_GIVER';
    const canDeleteUser = Boolean(form.roleUuid) && form.roleUuid !== user?.selectedRole && !isCaregiverForm;

    const getRedirectPathForRole = (role) => {
        if (role === 'ACCOUNT_ADMIN' || role === 'BCK_ADMIN') {
            return '/doctors';
        }
        if (role === 'CLINIC_CARE_GIVER') {
            return '/doctors';
        }
        if (role === 'PATIENT_CARE_GIVER' || role === 'PATIENT') {
            return '/caregivers';
        }
        return '/';
    };

    const onEdit = () => {
        setReadOnly(false);
    };

    const onCancel = () => {
        if (snapshot) {
            setForm(snapshot);
        }
        setError('');
        setReadOnly(true);
    };

    const handleToggleAvailabilityDay = (dayKey) => {
        setForm((prev) => ({
            ...prev,
            availabilityActiveDays: prev.availabilityActiveDays.includes(dayKey)
                ? prev.availabilityActiveDays.filter((item) => item !== dayKey)
                : [...prev.availabilityActiveDays, dayKey]
        }));
    };

    const onSubmit = async () => {
        if (!user.selectedRole || !form.roleUuid) {
            setError('Sem role ativa para editar utilizador.');
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            const payload = {
                role_uuid: form.roleUuid,
                name: form.name.trim() || undefined,
                birth_date: toApiDate(form.birthDate),
                phone: form.phone.trim() || undefined,
                address: {
                    street: form.street.trim() || undefined,
                    zip: form.zip.trim() || undefined,
                    city: form.city.trim() || undefined,
                    province: form.province.trim() || undefined,
                    country: form.country.trim().toUpperCase() || undefined
                }
            };

            if (isDoctorForm) {
                payload.user_data = {
                    professional_card: form.professionalCard.trim() || null,
                    professional_card_expiry: form.professionalCardExpiry.trim() || null,
                    workplace: form.workplace.trim() || null,
                    notes: form.notes.trim() || null,
                    [AVAILABILITY_ACTIVE_DAYS_KEY]: form.availabilityActiveDays,
                    [AVAILABILITY_NOTES_KEY]: form.availabilityNotes.trim() || null
                };
                payload.clinician_type = form.clinicianType || undefined;
                payload.call_priority = normalizeCallPriority(form.callPriority);
            } else if (isCaregiverForm) {
                payload.user_data = {
                    professional_status: form.professionalStatus.trim() || null,
                    nif: form.nif.trim() || null,
                    social_security: form.socialSecurity.trim() || null,
                    health_number: form.healthNumber.trim() || null,
                    occupation: form.occupation.trim() || null,
                    education: form.education.trim() || null,
                    active_since: toApiDate(form.activeSince) || null
                };
            }

            await updateUserByRole({
                userRoleUuid: user.selectedRole,
                payload
            });

            const nextSnapshot = { ...form };
            setSnapshot(nextSnapshot);
            setForm(nextSnapshot);
            setReadOnly(true);
        } catch (err) {
            setError(err?.response?.data?.description || 'Nao foi possivel atualizar utilizador.');
        } finally {
            setSubmitting(false);
        }
    };

    const onOpenDeleteDialog = () => {
        setDeleteDialogOpen(true);
    };

    const onCloseDeleteDialog = () => {
        if (deleting) {
            return;
        }
        setDeleteDialogOpen(false);
    };

    const onDeleteUser = async () => {
        if (!user.selectedRole || !form.roleUuid) {
            setError('Sem role ativa para eliminar utilizador.');
            return;
        }

        setDeleting(true);
        setError('');
        try {
            await deleteUserByRole({
                userRoleUuid: user.selectedRole,
                roleUuid: form.roleUuid
            });
            navigate(getRedirectPathForRole(form.role));
        } catch (err) {
            setError(err?.response?.data?.description || 'Não foi possível eliminar utilizador.');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: '#ffffff' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Paper
                    variant="outlined"
                sx={{
                    mt: 2,
                    p: { xs: 1.5, md: 2.5 },
                    bgcolor: '#ffffff',
                    borderRadius: 2
                }}
                >
                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <IconButton
                        sx={{
                            width: 40,
                            height: 40,
                            border: '1.5px solid #BDBDBD',
                            bgcolor: '#FFFFFF',
                            color: '#3D3D3D',
                            borderRadius: '12px',
                            '&:hover': {
                                bgcolor: '#F7F7F7'
                            }
                        }}
                        onClick={() => navigate(-1)}
                    >
                        <ArrowBackIosNewRoundedIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h4" fontWeight={700}>
                        Editar Utilizador
                    </Typography>
                </Stack>

                {!isDoctorForm && !isCaregiverForm ? (
                    <Typography color="error">
                        Role nao suportado para este formulario: {form.role || 'desconhecido'}.
                    </Typography>
                ) : null}

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                        gap: 2,
                        ...(readOnly ? readOnlyViewFieldSx : {})
                    }}
                >
                    <TextField
                        label="Nome"
                        placeholder="Nome completo"
                        fullWidth
                        value={form.name}
                        onChange={onFieldChange('name')}
                        disabled={readOnly || loading || submitting}
                    />
                    <TextField
                        label="Email"
                        placeholder="utilizador@dominio.com"
                        fullWidth
                        value={form.email}
                        disabled
                    />
                    <DatePicker
                        label="Data de Nascimento"
                        format="DD/MM/YYYY"
                        disabled={readOnly || loading || submitting}
                        value={toPickerValue(form.birthDate)}
                        onChange={(newValue) =>
                            onFieldChange('birthDate')({
                                target: {
                                    value:
                                        newValue && dayjs(newValue).isValid()
                                            ? dayjs(newValue).format('YYYY-MM-DD')
                                            : ''
                                }
                            })
                        }
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                disabled: readOnly || loading || submitting
                            }
                        }}
                    />
                    {isCaregiverForm ? (
                        <TextField
                            label="Situacao Profissional"
                            fullWidth
                            value={form.professionalStatus}
                            onChange={onFieldChange('professionalStatus')}
                            disabled={readOnly || loading || submitting}
                        />
                    ) : null}
                    <TextField
                        label="Morada"
                        placeholder="Rua / Avenida"
                        fullWidth
                        sx={{ gridColumn: { xs: 'auto', md: 'span 2' } }}
                        value={form.street}
                        onChange={onFieldChange('street')}
                        disabled={readOnly || loading || submitting}
                    />
                    <TextField
                        label="Código Postal"
                        placeholder="0000-000"
                        fullWidth
                        value={form.zip}
                        onChange={onFieldChange('zip')}
                        disabled={readOnly || loading || submitting}
                    />
                    <TextField
                        label="Cidade"
                        placeholder="Cidade"
                        fullWidth
                        value={form.city}
                        onChange={onFieldChange('city')}
                        disabled={readOnly || loading || submitting}
                    />
                    <TextField
                        label="Distrito / Provincia"
                        placeholder="Distrito ou provincia"
                        fullWidth
                        value={form.province}
                        onChange={onFieldChange('province')}
                        disabled={readOnly || loading || submitting}
                    />
                    <TextField
                        label="País"
                        placeholder="PT"
                        fullWidth
                        value={form.country}
                        onChange={onFieldChange('country')}
                        disabled={readOnly || loading || submitting}
                        inputProps={{ maxLength: 2 }}
                    />
                    <TextField
                        label="Telefone"
                        placeholder="912345678"
                        fullWidth
                        value={form.phone}
                        onChange={onFieldChange('phone')}
                        disabled={readOnly || loading || submitting}
                    />
                </Box>

                {isDoctorForm ? (
                    <>
                        <Divider sx={{ my: 4, borderColor: '#7f7f7f' }} />

                        <Typography variant="h4" fontWeight={700} mb={3}>
                            Profissional
                        </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                        gap: 2,
                        ...(readOnly ? readOnlyViewFieldSx : {})
                    }}
                >
                            <TextField
                                label="Cédula"
                                placeholder="Número da cédula"
                                fullWidth
                                value={form.professionalCard}
                                onChange={onFieldChange('professionalCard')}
                                disabled={readOnly || loading || submitting}
                            />
                            <TextField
                                label="Validade"
                                placeholder="dd/mm/aaaa"
                                fullWidth
                                value={form.professionalCardExpiry}
                                onChange={onFieldChange('professionalCardExpiry')}
                                disabled={readOnly || loading || submitting}
                            />
                            <Box />
                            <TextField
                                label="Local"
                                fullWidth
                                sx={{ gridColumn: { xs: 'auto', md: 'span 2' } }}
                                value={form.workplace}
                                onChange={onFieldChange('workplace')}
                                disabled={readOnly || loading || submitting}
                            />
                            <Box />
                            <FormControl fullWidth disabled={readOnly || loading || submitting}>
                                <InputLabel id="edit-user-especialidade-label">
                                    Especialidade
                                </InputLabel>
                                <Select
                                    labelId="edit-user-especialidade-label"
                                    label="Especialidade"
                                    value={form.clinicianType}
                                    onChange={onFieldChange('clinicianType')}
                                >
                                    {clinicianTypeOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControlLabel
                                sx={{
                                    m: 0,
                                    minHeight: 56,
                                    px: 1,
                                    border: '1px solid',
                                    borderColor: 'rgba(0, 0, 0, 0.23)',
                                    borderRadius: 1,
                                    alignItems: 'center'
                                }}
                                control={<Checkbox checked={form.role === 'ACCOUNT_ADMIN'} disabled />}
                                label="Coordenador"
                            />
                            <TextField
                                label="Observações"
                                placeholder="Notas relevantes"
                                fullWidth
                                multiline
                                minRows={2}
                                sx={{ gridColumn: { xs: 'auto', md: 'span 2' } }}
                                value={form.notes}
                                onChange={onFieldChange('notes')}
                                disabled={readOnly || loading || submitting}
                            />
                        </Box>

                        <Divider sx={{ my: 4, borderColor: '#7f7f7f' }} />

                        <Typography variant="h4" fontWeight={700} mb={3}>
                            Definições de Videochamada
                        </Typography>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                gap: 2
                            }}
                        >
                            <CallPriorityField
                                value={form.callPriority}
                                disabled={readOnly || loading || submitting}
                                onChange={(value) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        callPriority: value
                                    }))
                                }
                                sx={{ maxWidth: 380 }}
                            />
                        </Box>

                        <Divider sx={{ my: 4, borderColor: '#7f7f7f' }} />

                        <Typography variant="h4" fontWeight={700} mb={3}>
                            Horários
                        </Typography>

                        <Box sx={{ maxWidth: 420 }}>
                            <AvailabilityScheduleCard
                                activeDays={form.availabilityActiveDays}
                                notes={form.availabilityNotes}
                                editing={!readOnly && !loading && !submitting}
                                onToggleDay={handleToggleAvailabilityDay}
                                onNotesChange={(value) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        availabilityNotes: value
                                    }))
                                }
                            />
                        </Box>
                    </>
                ) : null}

                {isCaregiverForm ? (
                    <>
                        <Divider sx={{ my: 4, borderColor: '#7f7f7f' }} />
                        <Typography variant="h4" fontWeight={700} mb={3}>
                            Cuidador
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                gap: 2
                            }}
                        >
                            <TextField
                                label="Ocupacao"
                                fullWidth
                                value={form.occupation}
                                onChange={onFieldChange('occupation')}
                                disabled={readOnly || loading || submitting}
                            />
                            <TextField
                                label="Escolaridade"
                                fullWidth
                                value={form.education}
                                onChange={onFieldChange('education')}
                                disabled={readOnly || loading || submitting}
                            />
                            <Box />
                            <TextField
                                label="Ativo desde"
                                placeholder="dd/mm/aaaa"
                                fullWidth
                                value={form.activeSince}
                                onChange={onFieldChange('activeSince')}
                                disabled={readOnly || loading || submitting}
                            />
                        </Box>
                    </>
                ) : null}

                {error ? (
                    <Typography color="error" sx={{ mt: 2 }}>
                        {error}
                    </Typography>
                ) : null}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                    {readOnly ? (
                        <Stack direction="row" spacing={1}>
                            {canDeleteUser ? (
                                <Fab
                                    size="medium"
                                    sx={{ bgcolor: '#3D3D3D', color: '#fff', boxShadow: 'none' }}
                                    onClick={onOpenDeleteDialog}
                                    disabled={loading || submitting || deleting}
                                >
                                    <DeleteOutlineRoundedIcon />
                                </Fab>
                            ) : null}
                            <Fab
                                size="medium"
                                sx={{ bgcolor: '#D63731', color: '#fff', boxShadow: 'none' }}
                                onClick={onEdit}
                                disabled={loading || submitting || (!isDoctorForm && !isCaregiverForm)}
                            >
                                <Box
                                    component="img"
                                    src="/assets/logos-svg/edit.svg"
                                    alt="Editar"
                                    sx={{ width: 18, height: 18, display: 'block' }}
                                />
                            </Fab>
                        </Stack>
                    ) : (
                        <Stack direction="row" spacing={1}>
                            <Fab
                                variant="extended"
                                sx={actionSecondarySx}
                                onClick={onCancel}
                                disabled={submitting}
                            >
                                Cancelar
                            </Fab>
                            <Fab
                                variant="extended"
                                sx={actionPrimarySx}
                                onClick={onSubmit}
                                disabled={loading || submitting || (!isDoctorForm && !isCaregiverForm)}
                            >
                                {submitting ? 'A guardar...' : 'Guardar'}
                            </Fab>
                        </Stack>
                    )}
                </Box>

                <Dialog
                    open={deleteDialogOpen}
                    onClose={onCloseDeleteDialog}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{ sx: dialogPaperSx }}
                >
                    <DialogTitle sx={dialogTitleSx}>Confirmar eliminação</DialogTitle>
                    <DialogContent sx={dialogContentSx}>
                        <Typography color="#3D3D3D">
                            Tem a certeza que pretende eliminar o utilizador{' '}
                            <strong>{form.name || '-'}</strong>?
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={dialogActionsSx}>
                        <Button
                            onClick={onCloseDeleteDialog}
                            variant="outlined"
                            sx={actionSecondarySx}
                            disabled={deleting}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={onDeleteUser}
                            variant="contained"
                            sx={actionPrimarySx}
                            disabled={deleting}
                        >
                            {deleting ? 'A eliminar...' : 'Eliminar'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
            </LocalizationProvider>
        </Box>
    );
};

export default EditUser;
